/**
 * Executive brand picker — the landing screen.
 *
 * A card grid (not a dropdown): each card carries the identity an
 * executive recognizes the brand by — model (brand) name, subheader (e.g. indication),
 * and forecast freshness when the backend provides it. One brand at a
 * time (v1 constraint).
 */
import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
} from '@mui/material';

import { EXEC_CONTENT_CONTAINER_SX } from './layout';
import { ExecutiveBrand } from './types';

interface BrandPickerProps {
  brands: ExecutiveBrand[];
  greetingName?: string;
  onSelect: (brand: ExecutiveBrand) => void;
}

const greetingForNow = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const formatFreshness = (iso?: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `Updated ${date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

const BrandPicker: React.FC<BrandPickerProps> = ({
  brands,
  greetingName,
  onSelect,
}) => (
  <Box sx={{ ...EXEC_CONTENT_CONTAINER_SX, py: 6 }}>
    <Typography variant="h4" sx={{ mb: 1 }}>
      {greetingForNow()}
      {greetingName ? `, ${greetingName}` : ''}
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
      Select a brand to view forecasts and insights
    </Typography>

    {brands.length === 0 ? (
      <Typography variant="body1" color="text.secondary">
        No brands are configured for executive access yet. Please contact your
        Trinity team.
      </Typography>
    ) : (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 3,
        }}
      >
        {brands.map((brand) => {
          const freshness = formatFreshness(brand.updated_at);
          return (
            <Card key={brand.model_instance_id} variant="outlined">
              <CardActionArea
                onClick={() => onSelect(brand)}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* 20px, not h5's 24px (ruled 2026-09-07: a grid of 24px bold names read as loud as the 28px
                      page title). The theme ladder has no step between h5 24px and h6 18px, and 18px would sit one
                      pixel above the 17px subheader below it, so this is a local size override on h5's weight. */}
                  <Typography
                    variant="h5"
                    sx={{ fontSize: '1.25rem', mb: 0.5 }}
                  >
                    {brand.model_name}
                  </Typography>
                  {brand.subheader && (
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      {brand.subheader}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {brand.model_instance_name}
                    {freshness ? ` · ${freshness}` : ''}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    )}
  </Box>
);

export default BrandPicker;
